import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip } from '@mui/material'

import { ButtonRounded } from 'components/button'

import { theme } from 'styles/theme'

import { buildOpenTooltip } from './InvoiceOpenIndicator.utils'

interface InvoiceOpenIndicatorProps {
  viewed?: boolean
  viewsCount?: number
  lastViewedAt?: string | null
  onClick?: () => void
  /**
   * 'row' = ButtonRounded de 42px na coluna de ações da tabela.
   * 'header' = ícone inline ao lado do chip de status no modal de detalhe.
   */
  variant?: 'row' | 'header'
}

/**
 * SC-10890: indicador de abertura da fatura.
 *
 * O ícone já existia em três lugares (listagem de cobranças, mapper usado pelo
 * ModalInvoice e header do ModalChargeDetail), sempre `disabled` — só tooltip.
 * Este componente unifica as três cópias e o torna clicável onde há para onde ir.
 *
 * Cinza (nunca abriu) permanece não-clicável: abrir um histórico vazio para as
 * 16 mil cobranças nesse estado prometeria conteúdo que não existe.
 */
const InvoiceOpenIndicator = ({
  viewed,
  viewsCount,
  lastViewedAt,
  onClick,
  variant = 'row'
}: InvoiceOpenIndicatorProps) => {
  const title = buildOpenTooltip(viewed, viewsCount, lastViewedAt)
  const color = viewed ? theme.palette.primary.main : theme.palette.neutral[700]
  const clickable = !!viewed && !!onClick

  if (variant === 'header') {
    return (
      <Tooltip title={title} arrow enterTouchDelay={0}>
        <Box
          // Box clicável precisa de papel e foco próprios: o DESIGN.md proíbe
          // controle interativo sem outline de foco.
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          aria-label={clickable ? title : undefined}
          onClick={clickable ? onClick : undefined}
          onKeyDown={(event) => {
            if (!clickable) return
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onClick?.()
            }
          }}
          display="inline-flex"
          sx={{ cursor: clickable ? 'pointer' : 'default' }}
        >
          <Icon icon="doneAll" colorSX={color} />
        </Box>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={title} arrow placement="top" enterTouchDelay={0}>
      <Box sx={{ cursor: clickable ? 'pointer' : 'default' }}>
        <ButtonRounded
          aria-label={title}
          variant="transparent"
          disabled={!clickable}
          onClick={clickable ? onClick : undefined}
        >
          <Icon icon="doneAll" sx={{ color }} />
        </ButtonRounded>
      </Box>
    </Tooltip>
  )
}

export default InvoiceOpenIndicator
